import React, { useRef } from 'react'
import { Card, Typography, Flex, Button, Tag } from 'antd'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons'
import type { FlowStep } from '@/data'
import { DebtQuestionEditor } from '../shared/DebtQuestionEditor'
import { QualificationEditor } from '../shared/QualificationEditor'
import { FormStepEditor } from '../shared/FormStepEditor'
import { defaultSteps, isDebtStep, isFormStep } from './config'

const { Text } = Typography

interface SavingsEstimatesBuilderProps {
  formSteps: FlowStep[]
  onChange: (steps: FlowStep[]) => void
  onDeleteStep?: (stepId: number) => void
}

export const SavingsEstimatesBuilder: React.FC<
  SavingsEstimatesBuilderProps
> = ({ formSteps: steps, onChange, onDeleteStep }) => {
  const qualIdCounter = useRef(0)

  const debtIndex = steps.findIndex(isDebtStep)
  const debtStep = debtIndex !== -1 ? steps[debtIndex] : null

  const firstFormIndex = steps.findIndex(isFormStep)
  // Qualifications: everything between debt and first form step (this will change when ordinals are fixed)
  const qualStart = debtIndex !== -1 ? debtIndex + 1 : 0
  const qualEnd = firstFormIndex !== -1 ? firstFormIndex : steps.length
  const qualificationSteps = steps.slice(qualStart, qualEnd)

  const formSteps = firstFormIndex !== -1 ? steps.slice(firstFormIndex) : []

  const allFormInputIds = new Set<string>()
  formSteps.forEach((s) => s.inputs.forEach((i) => allFormInputIds.add(i.id)))
  const canRemoveDebt =
    allFormInputIds.has('birthdate') && allFormInputIds.has('address')

  const updateStep = (index: number, updated: FlowStep) => {
    const newSteps = [...steps]
    newSteps[index] = updated
    onChange(newSteps)
  }

  // --- Debt step ---
  const removeDebtStep = () => {
    if (!canRemoveDebt || debtIndex === -1) return
    const stepToRemove = steps[debtIndex]
    if (stepToRemove.id != null) onDeleteStep?.(stepToRemove.id)
    onChange(steps.filter((_, i) => i !== debtIndex))
  }

  const addDebtStep = () => {
    const newSteps = [...steps]
    newSteps.splice(0, 0, { ...defaultSteps[0] })
    onChange(newSteps)
  }

  // --- Qualification steps ---
  const addQualificationStep = () => {
    const n = ++qualIdCounter.current
    const newStep: FlowStep = {
      ordinal: steps.length + 1,
      headline: 'New Qualification Step',
      sub_headline: '',
      cta_button: '',
      inputs: [
        {
          id: `btn_yes_${n}`,
          label: 'Yes',
          type: 'radio',
          placeholder: null,
          required: false,
          action: 'progress',
        },
        {
          id: `btn_no_${n}`,
          label: 'No',
          type: 'radio',
          placeholder: null,
          required: false,
          action: 'downsell',
        },
      ],
    }
    // Insert before the first form step (or at end if no form steps)
    const insertAt = firstFormIndex !== -1 ? firstFormIndex : steps.length
    const newSteps = [...steps]
    newSteps.splice(insertAt, 0, newStep)
    onChange(newSteps)
  }

  const removeQualificationStep = (qualIndex: number) => {
    const actualIndex = qualStart + qualIndex
    const stepToRemove = steps[actualIndex]
    if (stepToRemove.id != null) onDeleteStep?.(stepToRemove.id)
    onChange(steps.filter((_, i) => i !== actualIndex))
  }

  // --- Form steps ---
  const updateFormSteps = (newFormSteps: FlowStep[]) => {
    // Replace everything from firstFormIndex onward with newFormSteps
    const prefix =
      firstFormIndex !== -1 ? steps.slice(0, firstFormIndex) : steps
    onChange([...prefix, ...newFormSteps])
  }

  const debtStepCount = debtStep ? 1 : 0

  return (
    <Card title="Savings Estimates Steps" style={{ maxWidth: 700 }}>
      <Flex vertical gap="middle" style={{ width: '100%' }}>
        <Text type="secondary">
          Configure the Savings Estimates funnel. The debt question can be
          removed if Date of Birth and Address fields are present (soft credit
          pull). You can add qualification and form steps.
        </Text>

        {/* Debt Step */}
        {debtStep ? (
          <Card
            size="small"
            title={
              <Flex gap="small" align="center">
                <Tag color="red">Step 1</Tag>
                <Text strong>Debt Question</Text>
              </Flex>
            }
            extra={
              canRemoveDebt && (
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={removeDebtStep}
                  size="small"
                >
                  Remove
                </Button>
              )
            }
            style={{ backgroundColor: '#fafafa' }}
          >
            <DebtQuestionEditor
              step={debtStep}
              onChange={(updated) => updateStep(debtIndex, updated)}
            />
          </Card>
        ) : (
          <Card size="small" style={{ backgroundColor: '#fafafa' }}>
            <Flex justify="space-between" align="center">
              <Text type="secondary">
                Debt question removed — using soft credit pull via Date of Birth
                + Address
              </Text>
              <Button size="small" onClick={addDebtStep}>
                Add Debt Question
              </Button>
            </Flex>
          </Card>
        )}

        {/* Qualification Steps */}
        {qualificationSteps.map((qStep, qualIndex) => {
          const actualIndex = qualStart + qualIndex
          return (
            <Card
              key={qStep.id ?? `qual-${qualIndex}`}
              size="small"
              title={
                <Flex gap="small" align="center">
                  <Tag color="blue">Step {debtStepCount + qualIndex + 1}</Tag>
                  <Text strong>Qualification</Text>
                </Flex>
              }
              extra={
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeQualificationStep(qualIndex)}
                  size="small"
                >
                  Remove
                </Button>
              }
              style={{ backgroundColor: '#fafafa' }}
            >
              <QualificationEditor
                step={qStep}
                onChange={(updated) => updateStep(actualIndex, updated)}
              />
            </Card>
          )
        })}

        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={addQualificationStep}
          style={{ width: '100%' }}
        >
          Add Qualification Step
        </Button>

        {/* Form Steps */}
        <FormStepEditor
          formSteps={formSteps}
          onChange={updateFormSteps}
          onDeleteStep={onDeleteStep}
        />

        <Text type="secondary" style={{ fontSize: 12 }}>
          {steps.length} step{steps.length !== 1 ? 's' : ''} configured
          {qualificationSteps.length > 0 &&
            ` (${qualificationSteps.length} qualification)`}
          {formSteps.length > 0 && ` (${formSteps.length} form)`}
        </Text>
      </Flex>
    </Card>
  )
}
